<template>
	<div
		ref="wrapper"
		class="edit-bar"
		:class="{
			'-mobile-portrait': isMobileWebApp && isMobileWebAppPortrait,
			'-mobile-landscape': isMobileWebApp && isMobileWebAppLandscape
		}"
		@mousedown="handleWrapperMouseDown"
		@touchstart="handleWrapperMouseDown"
	>
		<div
			ref="box"
			class="edit-bar__box"
			:class="{'-compacted': isCompacted}"
			:style="boxStyle"
			@mousedown.self="handleMouseDownBox"
			@touchstart.self="handleMouseDownBox"
		>
			<div
				v-if="!(isMobileWebApp && isMobileWebAppPortrait)"
				ref="info"
				class="edit-bar__info"
				:class="{'is-hidden': isInfoOverflowing}"
			>
				<div class="edit-bar__info-title">
					{{ durationLabel }}
				</div>
				<div class="edit-bar__info-description">
					{{ labels.name }}
				</div>
			</div>
			<div
				v-if="!isCompacted"
				ref="timestamp"
				class="edit-bar__timestamp"
				:style="timestampStyle"
				@mousedown.self="handleMouseDownTimestamp"
				@touchstart.self="handleMouseDownTimestamp"
			/>
			<div
				v-else
				ref="timestamp"
				class="edit-bar__timestamp"
				:style="timestampStyle"
				@mousedown.self="handleMouseDownBox"
				@touchstart.self="handleMouseDownBox"
			/>
			<div
				ref="left"
				class="edit-bar__handler -left"
				@mousedown.self="handleMouseDownLeft"
				@touchstart.self="handleMouseDownLeft"
			/>
			<div
				ref="right"
				class="edit-bar__handler -right"
				@mousedown.self="handleMouseDownRight"
				@touchstart.self="handleMouseDownRight"
			/>
		</div>

		<!-- mobile scrubber -->
		<div
			v-if="isMobileWebApp && isMobileWebAppPortrait"
			ref="needle"
			class="edit-bar__needle"
			:style="mobileScrubberStyle"
			@touchstart.stop.prevent="handleScrubberTouchStart"
		>
			<div class="edit-bar__grab-box" />
			<div
				ref="cursor"
				class="edit-bar__cursor"
			/>
		</div>
	</div>
</template>

<script>
import { mapActions } from 'vuex';

import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';
import { getTimeLabelFromSeconds } from '../../util/helpers';
import { EDIT_BAR_MIN_WIDTH } from './constants';
import { playerBus } from '@videotools/js/events';

const STATE = {
	IDLE:           'idle',
	RESIZE_LEFT:    'resize-left',
	RESIZE_RIGHT:   'resize-right',
	MOVE:           'move',
	MOVE_TIMESTAMP: 'move-timestamp',
	MARKING:        'marking',
};

const INFO_OVERFLOW_THRESHOLD = 8; // px

export default {
	name:  'EditBar',
	props: {
		eventDuration: {
			type:     Number,
			required: true,
		},
		tag: {
			type:    Object,
			default: () => null,
		},
		zoomLevel: {
			type:    Number,
			default: 1,
		},
		sportName: {
			type:    String,
			default: '',
		},
		isPaused: {
			type: Boolean,
		},
		elapsed: {
			type:    Number,
			default: 0,
		},
		isMobileWebApp: {
			type:    Boolean,
			default: false,
		},
		isMobileWebAppPortrait: {
			type:    Boolean,
			default: false,
		},
		isMobileWebAppLandscape: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			state:             STATE.IDLE,
			containerWidth:    0,
			startX:            null,
			realX:             0,
			realWidth:         0,
			forcedX:           null,
			forcedWidth:       null,
			forcedTimestampX:  null,
			cachedX:           null,
			cachedWidth:       null,
			cachedTimestampX:  null,
			markingDuration:   0,
			forcedStart:       0,
			forcedDuration:    0,
			isInfoOverflowing: false,
			isCompacted:       false,
			timestamp:         0,
			secondsBefore:     0,
			secondsAfter:      0,
		};
	},
	computed: {
		durationLabel() {
			return formatTimestampAsMinutesSeconds(this.forcedDuration);
		},
		scrollElement() {
			if (!this.$refs.wrapper) return null;
			return this.$refs.wrapper.parentNode;
		},
		containerElement() {
			if (!this.scrollElement) return null;
			return this.scrollElement.parentNode;
		},
		tagStart() {
			if (!this.tag) throw new Error('Cannot retrieve tag start from nonexisting tag.');
			return this.tag.time - this.tag.secondsBefore;
		},
		tagEnd() {
			if (!this.tag) throw new Error('Cannot retrieve tag start from nonexisting tag.');
			return this.tag.time + this.tag.secondsAfter;
		},
		tagDuration() {
			return this.tag.duration;
		},
		width() {
			return (this.tagDuration * 100) / this.eventDuration;
		},
		left() {
			return (this.tagStart * 100) / this.eventDuration;
		},
		timestampLeft() {
			const value = (this.tag.secondsBefore * 100) / this.tagDuration;
			return Number.isFinite(value) ? value : 0;
		},
		labels() {
			return {
				name: this.transTagLabel(this.tag.name, this.sportName, this.tag.tag?.type),
				time: getTimeLabelFromSeconds(this.tag.time),
			};
		},
		boxStyle() {
			let left = 0;
			if (this.forcedX !== null) {
				left = `${this.forcedX}px`;
			} else if (this.cachedX !== null) {
				left = `${this.cachedX}px`;
			} else {
				left = `${this.left}%`;
			}
			let width = 0;
			if (this.forcedWidth !== null) {
				width = `${this.forcedWidth}px`;
			} else if (this.cachedWidth !== null) {
				width = `${this.cachedWidth}px`;
			} else {
				width = `${this.width}%`;
			}

			return {
				left,
				minWidth: `${EDIT_BAR_MIN_WIDTH}px`,
				width,
			};
		},
		timestampStyle() {
			let left = 0;
			if (this.isCompacted) {
				return { marginLeft: '50%' };
			}
			if (this.forcedTimestampX !== null) {
				left = `${this.forcedTimestampX}px`;
			} else if (this.cachedTimestampX !== null) {
				left = `${this.cachedTimestampX}px`;
			} else {
				left = `${this.left}%`;
			}

			return {
				marginLeft: left,
			};
		},
		scrubberOverlap() {
			return this.elapsed >= this.forcedStart && this.elapsed <= this.forcedStart + this.forcedDuration;
		},
		mobileScrubberStyle() {
			const left = Math.round((this.elapsed * 100) / this.eventDuration);
			return { display: this.scrubberOverlap && this.isPaused ? 'none' : 'block', left: `${left}%` };
		},
	},
	watch: {
		zoomLevel() {
			this.redraw();
		},
		tag() {
			this.redraw();
		},
		tagStart(value) {
			this.forcedStart = value;
		},
		tagDuration(value) {
			this.forcedDuration = value;
		},
	},
	mounted() {
		this.cache();
		playerBus.subscribePosition(this.handleSeekEvent);
		this.seek(this.tagStart);
		playerBus.pause();

		this.forcedStart = this.tagStart;
		this.forcedDuration = this.tagDuration;
	},
	destroyed() {
		playerBus.unsubscribePosition(this.handleSeekEvent);
	},
	methods: {
		...mapActions({
			updateSelectedTagProps: 'eventTagEdit/updateSelectedTagProps',
		}),
		redraw() {
			this.cachedX = null;
			this.cachedWidth = null;
			this.cachedTimestampX = null;
			this.$nextTick(() => {
				setImmediate(() => {
					this.cache();
					this.adjustDisplayedInfo();
					this.updateCompactMode();
				});
			});
		},
		cache() {
			const boxRef = this.$refs.box;
			if (!boxRef) return;
			const boxRect = boxRef.getBoundingClientRect();
			this.cachedX = boxRef.offsetLeft;
			this.cachedWidth = boxRect.width;
			this.cachedTimestampX = boxRect.width / (100 / this.timestampLeft);

			this.timestamp = this.tag.timestamp;
			this.secondsBefore = this.tag.secondsBefore;
			this.secondsAfter = this.tag.secondsAfter;
		},
		handleWrapperMouseDown() {
			document.addEventListener('mousemove', this.handleMouseMove);
			document.addEventListener('mouseup', this.handleMouseUp);
			document.addEventListener('touchmove', this.handleMouseMove);
			document.addEventListener('touchend', this.handleMouseUp);
		},
		handleMouseDown(e) {
			this.startX = e.touches ? e.touches[0].clientX : e.clientX;
			const boxRef = this.$refs.box;
			const boxRect = boxRef.getBoundingClientRect();
			this.realX = boxRef.offsetLeft;
			this.realWidth = boxRect.width;
			this.forcedX = this.realX;
			this.forcedWidth = this.realWidth;
		},
		handleMouseDownLeft(e) {
			this.state = STATE.RESIZE_LEFT;
			this.handleMouseDown(e);
			this.$emit('resizing', 'left');
		},
		handleMouseDownRight(e) {
			this.state = STATE.RESIZE_RIGHT;
			this.handleMouseDown(e);
			this.$emit('resizing', 'right');
		},
		handleMouseDownBox(e) {
			this.state = STATE.MOVE;
			this.handleMouseDown(e);
		},
		handleMouseDownTimestamp(e) {
			if (this.isCompacted) return;
			this.state = STATE.MOVE_TIMESTAMP;
			this.handleMouseDown(e);
		},
		handleMouseMove(e) {
			if (this.state === STATE.IDLE) return;
			const distance = (e.touches ? e.touches[0].clientX : e.clientX) - this.startX;

			if (this.state === STATE.MOVE) {
				const maxX = this.scrollElement.offsetWidth - this.realWidth;
				const x = Math.max(0, Math.min(maxX, this.realX + distance));
				this.forcedX = x;
			}

			if (this.state === STATE.RESIZE_LEFT) {
				const x = Math.min(this.realX + this.realWidth - EDIT_BAR_MIN_WIDTH, this.realX + distance);
				this.forcedX = Math.max(0, x);
				if (x >= 0) {
					this.forcedWidth = this.realWidth - distance;
					this.forcedTimestampX = Math.max(this.cachedTimestampX - distance - EDIT_BAR_MIN_WIDTH, 0);
				}
			}

			if (this.state === STATE.RESIZE_RIGHT) {
				this.forcedWidth = Math.min(this.scrollElement.offsetWidth - this.realX, this.realWidth + distance);
			}

			if (this.state === STATE.MOVE_TIMESTAMP) {
				const start = this.cachedTimestampX === null ? this.realWidth / (100 / this.timestampLeft) : this.cachedTimestampX;
				const val = start + distance;
				if (val < 0) {
					this.forcedTimestampX = 0;
				} else if (val > this.realWidth) {
					this.forcedTimestampX = this.realWidth;
				} else {
					this.forcedTimestampX = val;
				}
			}

			if ([STATE.RESIZE_LEFT, STATE.RESIZE_RIGHT].includes(this.state)) {
				// Use either the cached timestamp X or the forced timestamp X for comparison.
				// The cached timestamp x may not exactly reflect the current value, as it could be modified during left resizing
				// and then only forcedTimestampX will be updated.
				const timestampX = this.forcedTimestampX ?? this.cachedTimestampX;

				if (timestampX > this.forcedWidth) {
					this.forcedTimestampX = Math.max(0, this.forcedWidth);
				}

				this.adjustDisplayedInfo();
				this.updateCompactMode();
			}

			if ([STATE.MOVE, STATE.RESIZE_LEFT, STATE.RESIZE_RIGHT, STATE.MOVE_TIMESTAMP].includes(this.state)) {
				const x = this.forcedX !== null ? this.forcedX : this.cachedX;
				const width = this.forcedWidth !== null ? this.forcedWidth : this.cachedWidth;
				const timestamp = this.forcedTimestampX !== null ? this.forcedTimestampX : this.cachedTimestampX;
				const total = this.scrollElement.offsetWidth;
				const xRatio = x / total;
				const widthRatio = width / total;
				const timestampRatio = timestamp / width;
				const xTime = this.eventDuration * xRatio;
				const widthTime = this.eventDuration * widthRatio;
				const timestampTime = widthTime * timestampRatio;
				const secondsBefore = timestampTime;
				const secondsAfter = widthTime - timestampTime;
				const time = xTime + secondsBefore;

				if (width < EDIT_BAR_MIN_WIDTH) return;

				this.timestamp = time;
				this.secondsBefore = secondsBefore;
				this.secondsAfter = secondsAfter;
				this.forcedStart = time - secondsBefore;
				this.forcedDuration = secondsBefore + secondsAfter;

				switch (this.state) {
				case STATE.MOVE:
				case STATE.MOVE_TIMESTAMP:
					this.seek(time);
					break;
				case STATE.RESIZE_LEFT:
					this.seek(time - secondsBefore);
					break;
				case STATE.RESIZE_RIGHT:
					this.seek(time + secondsAfter);
					break;
				default:
					break;
				}
			}
		},
		handleMouseUp() {
			if (this.forcedX !== null) this.cachedX = this.forcedX;
			if (this.forcedWidth !== null) this.cachedWidth = this.forcedWidth;
			if (this.forcedTimestampX !== null) this.cachedTimestampX = this.forcedTimestampX;
			this.forcedX = null;
			this.forcedWidth = null;
			this.forcedTimestampX = null;

			document.removeEventListener('mousemove', this.handleMouseMove);
			document.removeEventListener('mouseup', this.handleMouseUp);
			document.removeEventListener('touchmove', this.handleMouseMove);
			document.removeEventListener('touchend', this.handleMouseUp);

			this.updateSelectedTagProps({
				timestamp:     this.timestamp,
				secondsBefore: this.secondsBefore,
				secondsAfter:  this.secondsAfter,
			});

			switch (this.state) {
			case STATE.RESIZE_LEFT:
				this.seek(Math.round(this.timestamp) - Math.round(this.secondsBefore));
				break;
			case STATE.RESIZE_RIGHT:
				this.seek(Math.round(this.timestamp + this.secondsAfter));
				break;
			case STATE.MOVE_TIMESTAMP:
			case STATE.MOVE:
				this.seek(Math.round(this.timestamp));
				break;
			}

			this.state = STATE.IDLE;
		},
		seek(time) {
			playerBus.seek(time);
			// Emit event to parent component so it can handle seeking
			this.$emit('marking-update', { time, duration: this.forcedDuration });
		},
		handleSeekEvent(time) {
			if (time > this.tagEnd && time < this.tagEnd + 0.3) {
				playerBus.pause();
				this.$emit('pause');
			}
		},
		handleScrubberTouchStart(event) {
			this.$emit('scrubber-touch-start', event);
		},
		adjustDisplayedInfo() {
			const infoEl = this.$refs.info;
			const boxEl = this.$refs.box;
			if (!infoEl || !boxEl) {
				return;
			}
			this.isInfoOverflowing = boxEl.offsetWidth - (infoEl.offsetWidth + infoEl.offsetLeft) < INFO_OVERFLOW_THRESHOLD;
		},
		updateCompactMode() {
			const boxEl = this.$refs.box;
			this.isCompacted = boxEl?.offsetWidth <= EDIT_BAR_MIN_WIDTH || false;
		},
	},
};
</script>

<style lang="scss" scoped>
@import "./timeline.scss";
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.edit-bar {
	position: absolute;
	top: 0;
	width: 100%;
	height: 100%;
	display: block;
	backface-visibility: hidden;
}

.edit-bar__box {
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	margin-top: 8px;
	height: calc(100% - 16px);
	will-change: left, width;
	background-color: var(--color-grey-900);
	border: 1px solid var(--color-grey-700);
	border-radius: 4px;

	.edit-bar.-mobile-portrait & {
		background-color: rgba(255, 66, 66, 0.2);
	}
}

.edit-bar__timestamp {
	position: relative;
	width: 1px;
	height: 100%;
	z-index: 2;
	background-color: var(--color-hype-yellow);
	will-change: margin-left;
	cursor: col-resize;

	&::before {
		.edit-bar__box:not(.-compacted) & {
			content: "";
			position: absolute;
			top: calc(50% - 12px);
			right: -2px;
			width: 0;
			height: 24px;
			display: block;
			border: 3px solid var(--color-hype-yellow);
			border-radius: 4px;
		}
	}

	.edit-bar__box.-compacted & {
		cursor: pointer;
	}
}

.edit-bar__info {
	position: absolute;
	top: 24px;
	left: 24px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	color: var(--color-white);
	font-size: 16px;
	user-select: none;
	overflow: hidden;
	flex: 0 0 auto;

	&.is-hidden {
		visibility: hidden;
	}
}

.edit-bar__info-title {
	@include text-md-1(true);
	margin-bottom: 2px;
}

.edit-bar__info-description {
	@include text-sm-1;
	color: var(--color-grey-300);
}

.edit-bar__handler {
	position: absolute;
	top: 0;
	height: 100%;
	cursor: col-resize;

	&::before {
		position: absolute;
		top: calc(50% - 12px);
		display: block;
		content: "";
		width: 0;
		height: 24px;
		border: 2px solid var(--color-grey-500);
		border-radius: 4px;
	}

	&.-left {
		left: 0;

		&::before {
			left: -2px;
		}

	}

	&.-right {
		right: 0;

		&::before {
			right: -2px;
		}
	}
}

.edit-bar__needle {
	position: absolute;
	top: 0;
	width: 1px;
	height: 100%;
	background: $timeline-progress-needle-color;
	z-index: 10;
	pointer-events: none;
}
</style>
